import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Get file icon based on file extension
 * @param {string} fileName - The file name
 * @returns {JSX.Element} - The icon component
 */
const getFileIcon = (fileName) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: '#D32F2F' }} />;
    case 'xlsx':
    case 'xls':
      return <TableChartIcon sx={{ fontSize: 40, color: '#1E8E3E' }} />;
    case 'docx':
    case 'doc':
      return <ArticleIcon sx={{ fontSize: 40, color: '#1A73E8' }} />;
    default:
      return <DescriptionIcon sx={{ fontSize: 40, color: '#5F6368' }} />;
  }
};

/**
 * Format file size to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
const formatFileSize = (bytes, unknownLabel) => {
  if (!bytes) return unknownLabel;
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Format date to locale string
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date
 */
const formatDate = (dateString, language, unknownLabel) => {
  if (!dateString) return unknownLabel;
  
  return new Date(dateString).toLocaleDateString(language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * DocumentCard component for displaying document information
 * @param {Object} props - Component props
 * @param {Object} props.document - Document object
 * @param {Function} props.onDownload - Callback function when download is clicked
 */
const DocumentCard = ({ document, onDownload }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { t, language } = useLanguage();

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownload(document);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ mr: 2 }}>{getFileIcon(document.name)}</Box>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Tooltip title={document.name}>
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {document.name}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('documentSizeLabel')}: {formatFileSize(document.size, t('unknownSize'))}
        </Typography>
        {document.lastModifiedDateTime && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('documentModifiedLabel')}: {formatDate(document.lastModifiedDateTime, language, t('unknownDate'))}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', p: 1.5 }}>
        <Tooltip title={t('downloadButton')}>
          <span>
            <IconButton
              onClick={handleDownload}
              size="small"
              color="primary"
              disabled={isDownloading}
              aria-label={t('downloadDocumentAriaLabel')}
            >
              {isDownloading ? (
                <CircularProgress size={20} />
              ) : (
                <DownloadIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default DocumentCard;
